import { AccountClient, UserInfoDto } from '@core/api-client';
import {
  patchState,
  signalStore, withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { unwrap } from '@core/extensions';
import { tapResponse } from '@ngrx/operators';
import { UiNotificationService } from '@/ui-notifications/ui-notification.service';
import { JWTTokenService } from '@core/services/jwt-token.service';

type UserStoreState = {
  user: UserInfoDto;
  loading: boolean;
}

export const UserStore = signalStore(
  {
    providedIn: 'root',
    protectedState: true,
  },
  withState<UserStoreState>(() => ({
    user: {} as UserInfoDto,
    loading: false,
  })),
  withComputed((store) => ({
    userName: computed(() => store.user()?.userName || ''),
    firstName: computed(() => store.user()?.firstName || ''),
    lastName: computed(() => store.user()?.lastName || ''),
    email: computed(() => store.user()?.email || ''),
    phoneNumber: computed(() => store.user()?.phoneNumber || ''),
    isFromUsa: computed(() => store.user()?.isFromUsa || false),
  })),
  withMethods((store, client = inject(AccountClient), notifications = inject(UiNotificationService)) => ({
    refresh: rxMethod<void>(pipe(
      debounceTime(300),
      tap(() => patchState(store, { loading: true })),
      switchMap(() =>
        client.getUserInfo().pipe(
          unwrap<UserInfoDto>(),
          tapResponse({
            next: (result) => patchState(store, { user: result, loading: false }),
            error: (err) => {
              patchState(store, { loading: false });
              notifications.error(err);
            },
          })
        )
      )
    )),
    clear: () => patchState(store, { user: {} as UserInfoDto }),
  })),
  withHooks({
    onInit(store, jwtService = inject(JWTTokenService)) {
      if (jwtService.isTokenAvailable()) {
        store.refresh();
      }
    },
  }),
);

export type UserStore = InstanceType<
  typeof UserStore
>;
